/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';
import { combineReducers } from 'redux';

import * as actionTypes from './actionTypes';

const defaultStatus = {
  loading: 0,
  error: null,
  lastFetched: null,
  step: 0,
  isAutoProvisioning: false
};

// eslint-disable-next-line max-statements
// eslint-disable-next-line complexity
const instances = (state = [], action) => {
  switch (action.type) {
    case actionTypes.FETCH_DISCOVERY_INSTANCES_SUCCESS:
    case actionTypes.CREATE_DISCOVERY_INSTANCE_SUCCESS: {
      // use cached collection for existing instances
      const tempDict = {};
      state.forEach(instance => {
        tempDict[instance.id] = {
          collections: instance.collections,
          collectionsLimitReached: instance.collectionsLimitReached
        };
      });
      action.instances.forEach(instance => {
        instance.collections =
          instance.collections && instance.collections.length > 0
            ? instance.collections
            : _.get(tempDict, `[${instance.id}].collections`, []);
        instance.collectionsLimitReached = _.get(
          tempDict,
          `[${instance.id}].collectionsLimitReached`,
          false
        );
      });
      return [...action.instances];
    }
    case actionTypes.FETCH_DISCOVERY_COLLECTIONS_SUCCESS: {
      const targetInstance = state.find(i => i.id === action.instance.id);
      if (targetInstance) {
        targetInstance.collections = [...action.collections];
        targetInstance.collectionsLimitReached = action.collectionsLimitReached;
        return [...state];
      } else {
        action.instance.collections = [...action.collections];
        action.instance.collectionsLimitReached =
          action.collectionsLimitReached;
        return [...state, action.instance];
      }
    }
    case actionTypes.FETCH_DISCOVERY_COLLECTION_DETAIL: {
      const instance = state.find(i => i.id === action.instanceId);
      if (instance && instance.collections) {
        const collection = instance.collections.find(
          c => c.collection_id === action.collectionId
        );
        Object.assign(collection, action.collectionDetail);
      }
      return [...state];
    }
    case actionTypes.FETCH_DISCOVERY_COLLECTIONS_ERROR: {
      const instance = state.find(i => i.id === action.instanceId);
      if (instance) {
        instance.unavailable = true;
      }
      return [...state];
    }
    case actionTypes.FETCH_DISCOVERY_V2_PROJECTS_COLLECTIONS_SUCCESS: {
      const targetInstance = state.find(i => i.id === action.instance.id);
      if (targetInstance) {
        targetInstance.projects = [...action.projects];
        return [...state];
      }
      return state;
    }

    default:
      return state;
  }
};

const unavailableInstances = (state = [], action) => {
  switch (action.type) {
    case actionTypes.FETCH_DISCOVERY_INSTANCES_SOME_UNAVAILABLE:
      return [...action.unavailableInstances];
    default:
      return state;
  }
};

const status = (state = defaultStatus, action) => {
  switch (action.type) {
    case actionTypes.FETCH_DISCOVERY_INSTANCES_START:
      return { ...state, loading: state.loading + 1, error: null };
    case actionTypes.FETCH_DISCOVERY_INSTANCES_SUCCESS:
      return { ...state, loading: state.loading - 1, lastFetched: Date.now() };
    case actionTypes.FETCH_DISCOVERY_INSTANCES_ERROR:
      return { ...state, loading: state.loading - 1, error: action.error };
    case actionTypes.FETCH_DISCOVERY_COLLECTIONS_ERROR:
      return { ...state, error: action.error };
    case actionTypes.CREATE_DISCOVERY_INSTANCE_START:
    case actionTypes.CREATE_DISCOVERY_INSTANCE_CHECK_STATUS:
    case actionTypes.CREATE_DISCOVERY_INSTANCE_CREATE_CREDENTIALS:
    case actionTypes.CREATE_DISCOVERY_INSTANCE_CREATE_ENVIRONMENT:
      return { ...state, isAutoProvisioning: true, step: action.step };
    case actionTypes.CREATE_DISCOVERY_INSTANCE_SUCCESS:
      return { ...state, isAutoProvisioning: false };
    case actionTypes.CREATE_DISCOVERY_INSTANCE_ERROR:
      return { ...state, isAutoProvisioning: false, error: action.error };
    default:
      return state;
  }
};

export const reducer = combineReducers({
  instances,
  status,
  unavailableInstances
});

export default reducer;
